import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getIncidentReports,
  updateIncidentReportStatus,
  getEmergencyNumbers,
  saveEmergencyNumber,
  deleteEmergencyNumber,
} from '../api/admin';
import Pagination from '../components/Pagination';
import styles from './SafetyReports.module.css';

const STATUS_OPTIONS = ['submitted', 'in_review', 'resolved', 'dismissed'];
const TYPE_OPTIONS = ['accident', 'safety_concern', 'harassment', 'vehicle_issue', 'other'];
const ROLE_OPTIONS = ['driver', 'rider', 'vehicle_owner'];
// Open Question 38 — the 4 anomaly categories reports can optionally be tagged with.
const CATEGORY_OPTIONS = ['safety_behaviour', 'vehicle_driving', 'recordings_data', 'other'];
const CATEGORY_LABEL = {
  safety_behaviour: 'Safety & behaviour',
  vehicle_driving: 'Vehicle & driving',
  recordings_data: 'Recordings & data',
  other: 'Other',
};

const STATUS_LABEL = {
  submitted: 'Submitted',
  in_review: 'In review',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
};

function statusClass(status) {
  if (status === 'resolved') return 'stResolved';
  if (status === 'dismissed') return 'stDismissed';
  if (status === 'in_review') return 'stInReview';
  return 'stSubmitted';
}

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ─── Incident Reports tab ─────────────────────────────────────────────────────

function IncidentReportsTab() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [reporterRole, setReporterRole] = useState('');
  const [category, setCategory] = useState('');
  const [selected, setSelected] = useState(null);

  const params = { page, limit: 20 };
  if (status) params.status = status;
  if (type) params.type = type;
  if (reporterRole) params.reporter_role = reporterRole;
  if (category) params.anomaly_category = category;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['incident-reports', params],
    queryFn: () => getIncidentReports(params).then((r) => r.data),
    keepPreviousData: true,
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status: next }) => updateIncidentReportStatus(id, next),
    onSuccess: (res) => {
      toast.success('Status updated');
      qc.invalidateQueries({ queryKey: ['incident-reports'] });
      setSelected((cur) => (cur && cur.id === res.data?.report?.id ? res.data.report : cur));
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to update status'),
  });

  const reports = data?.reports || [];
  const pagination = data?.pagination;

  return (
    <>
      <div className={styles.filters}>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className={styles.select}>
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
        <select value={type} onChange={(e) => { setType(e.target.value); setPage(1); }} className={styles.select}>
          <option value="">All types</option>
          {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
        <select value={reporterRole} onChange={(e) => { setReporterRole(e.target.value); setPage(1); }} className={styles.select}>
          <option value="">All reporters</option>
          {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
        </select>
        <select value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }} className={styles.select}>
          <option value="">All anomaly categories</option>
          {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
        </select>
      </div>

      {isLoading && <p className={styles.msg}>Loading…</p>}
      {isError && (
        <p className={styles.msgError}>
          Failed to load incident reports: {error?.response?.data?.error || error?.message}
        </p>
      )}

      {!isLoading && !isError && reports.length === 0 && (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>✅</div>
          <p>No incident reports match this filter.</p>
        </div>
      )}

      {reports.length > 0 && (
        <div className={styles.list}>
          {reports.map((r) => (
            <div key={r.id} className={styles.card} onClick={() => setSelected(r)}>
              <div className={styles.cardHeader}>
                <span className={styles.cardType}>{r.type.replace(/_/g, ' ')}</span>
                <span className={`${styles.statusChip} ${styles[statusClass(r.status)]}`}>{STATUS_LABEL[r.status]}</span>
              </div>
              {r.anomaly_category && (
                <div className={styles.cardMeta}>
                  <span>{CATEGORY_LABEL[r.anomaly_category] || r.anomaly_category}{r.anomaly_type ? ` — ${r.anomaly_type.replace(/_/g, ' ')}` : ''}</span>
                </div>
              )}
              <div className={styles.cardMeta}>
                <span>Reporter: {r.reporter?.full_name || r.reporter?.email || '—'} ({r.reporter_role.replace(/_/g, ' ')})</span>
                <span>{fmt(r.created_at)}</span>
              </div>
              {r.vehicle && (
                <div className={styles.cardMeta}>
                  <span>Vehicle: {r.vehicle.make} {r.vehicle.model} · {r.vehicle.license_plate}</span>
                </div>
              )}
              {r.description && <p className={styles.cardDesc}>{r.description}</p>}
            </div>
          ))}
        </div>
      )}

      <Pagination page={pagination?.page || 1} pages={pagination?.pages || 1} onPage={setPage} />

      {selected && (
        <div className={styles.overlay} onClick={() => setSelected(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Incident report</h2>
              <button className={styles.closeBtn} onClick={() => setSelected(null)} aria-label="Close">✕</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.detailRow}><span>Type</span><span>{selected.type.replace(/_/g, ' ')}</span></div>
              {selected.anomaly_category && (
                <div className={styles.detailRow}><span>Anomaly category</span><span>{CATEGORY_LABEL[selected.anomaly_category] || selected.anomaly_category}</span></div>
              )}
              {selected.anomaly_type && (
                <div className={styles.detailRow}><span>Specific reason</span><span>{selected.anomaly_type.replace(/_/g, ' ')}</span></div>
              )}
              <div className={styles.detailRow}><span>Status</span><span>{STATUS_LABEL[selected.status]}</span></div>
              <div className={styles.detailRow}><span>Reporter</span><span>{selected.reporter?.full_name || '—'} ({selected.reporter?.email || '—'})</span></div>
              <div className={styles.detailRow}><span>Reporter role</span><span>{selected.reporter_role.replace(/_/g, ' ')}</span></div>
              {selected.trip_type && <div className={styles.detailRow}><span>Trip type</span><span>{selected.trip_type.replace(/_/g, ' ')}</span></div>}
              {selected.trip_id && <div className={styles.detailRow}><span>Trip ref</span><span><code>{String(selected.trip_id).slice(0, 12)}…</code></span></div>}
              {selected.vehicle && (
                <div className={styles.detailRow}>
                  <span>Vehicle</span>
                  <span>{selected.vehicle.make} {selected.vehicle.model} · {selected.vehicle.license_plate}</span>
                </div>
              )}
              <div className={styles.detailRow}><span>Reported</span><span>{fmt(selected.created_at)}</span></div>
              <div className={styles.detailRow}><span>Last updated</span><span>{fmt(selected.updated_at)}</span></div>
              {selected.description && (
                <div className={styles.descBox}>
                  <span className={styles.descLabel}>Description</span>
                  <p>{selected.description}</p>
                </div>
              )}

              <div className={styles.statusActions}>
                <span className={styles.descLabel}>Change status</span>
                <div className={styles.statusBtnRow}>
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s}
                      className={`${styles.statusBtn} ${selected.status === s ? styles.statusBtnActive : ''}`}
                      disabled={selected.status === s || statusMutation.isPending}
                      onClick={() => statusMutation.mutate({ id: selected.id, status: s })}
                    >
                      {STATUS_LABEL[s]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Emergency Numbers tab ────────────────────────────────────────────────────

function EmergencyNumbersTab() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ country_code: '', number: '', label: '' });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['emergency-numbers'],
    queryFn: () => getEmergencyNumbers().then((r) => r.data),
  });

  const saveMutation = useMutation({
    mutationFn: (body) => saveEmergencyNumber(body),
    onSuccess: () => {
      toast.success('Saved');
      qc.invalidateQueries({ queryKey: ['emergency-numbers'] });
      setForm({ country_code: '', number: '', label: '' });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to save'),
  });

  const deleteMutation = useMutation({
    mutationFn: (countryCode) => deleteEmergencyNumber(countryCode),
    onSuccess: () => {
      toast.success('Deleted');
      qc.invalidateQueries({ queryKey: ['emergency-numbers'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to delete'),
  });

  const numbers = data?.emergency_numbers || [];

  return (
    <>
      <p className={styles.subtitle}>
        Looked up by the rider/driver/vehicle-owner Safety Toolkit from the account's country. The
        <strong> EU</strong> row is the fallback used when a country has no entry of its own.
      </p>

      <form
        className={styles.addForm}
        onSubmit={(e) => {
          e.preventDefault();
          if (!form.country_code.trim() || !form.number.trim()) return;
          saveMutation.mutate(form);
        }}
      >
        <input
          className={styles.input}
          placeholder="Country code (e.g. FR)"
          maxLength={2}
          value={form.country_code}
          onChange={(e) => setForm({ ...form, country_code: e.target.value.toUpperCase() })}
        />
        <input
          className={styles.input}
          placeholder="Number (e.g. 112)"
          value={form.number}
          onChange={(e) => setForm({ ...form, number: e.target.value })}
        />
        <input
          className={styles.input}
          placeholder="Label (optional)"
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
        />
        <button className={styles.btnPrimary} type="submit" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving…' : 'Add / update'}
        </button>
      </form>

      {isLoading && <p className={styles.msg}>Loading…</p>}
      {isError && <p className={styles.msgError}>Failed to load emergency numbers.</p>}

      {!isLoading && !isError && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Country</th>
                <th>Number</th>
                <th>Label</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {numbers.length === 0 && (
                <tr><td colSpan={5} className={styles.empty}>No emergency numbers configured.</td></tr>
              )}
              {numbers.map((n) => (
                <tr key={n.country_code}>
                  <td><strong>{n.country_code}</strong></td>
                  <td>{n.number}</td>
                  <td>{n.label || '—'}</td>
                  <td className={styles.date}>{fmt(n.updated_at)}</td>
                  <td>
                    <button
                      className={styles.editBtn}
                      onClick={() => setForm({ country_code: n.country_code, number: n.number, label: n.label || '' })}
                    >
                      Edit
                    </button>
                    <button
                      className={styles.deleteBtn}
                      disabled={n.country_code === 'EU' || deleteMutation.isPending}
                      title={n.country_code === 'EU' ? 'The EU fallback cannot be deleted' : 'Delete'}
                      onClick={() => {
                        if (window.confirm(`Remove the emergency number for ${n.country_code}?`)) {
                          deleteMutation.mutate(n.country_code);
                        }
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ─── Page shell ────────────────────────────────────────────────────────────────

export default function SafetyReports() {
  const [tab, setTab] = useState('incidents');

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Safety Center</h1>
      <p className={styles.subtitle}>Incident reports and emergency numbers submitted from the app's Safety Toolkit.</p>

      <div className={styles.tabs}>
        <button className={`${styles.tabBtn} ${tab === 'incidents' ? styles.tabActive : ''}`} onClick={() => setTab('incidents')}>
          Incident Reports
        </button>
        <button className={`${styles.tabBtn} ${tab === 'numbers' ? styles.tabActive : ''}`} onClick={() => setTab('numbers')}>
          Emergency Numbers
        </button>
      </div>

      {tab === 'incidents' ? <IncidentReportsTab /> : <EmergencyNumbersTab />}
    </div>
  );
}
